import { FETCH_PRODUCTS_TYPE } from '../actions';
import createFetchReducer, {
  createItemSetsFromSlicesGetter,
  createItemSetsFromSliceContentGetter,
} from './createFetchReducer';
import filterUnique from '../helpers/filterUnique';
import stringToArray from '../helpers/stringToArray';
import sanitizeFilterValue from '../helpers/sanitizeFilterValue';
import SliceType from '../constants/sliceType';

const initialState = {};
const SUPPORTED_SLICE_TYPES = ['catalog_tray', 'retail_tray', 'sponsored_brand_products'];
const filterKeys = ['new', 'gender', 'category', 'brand', 'campaign', 'color', 'shop'];
const apiFilterKeys = ['sku', ...filterKeys.map(key => `filter_${key}`)];

const getItemSetsFromFilteredResultsSlice = createItemSetsFromSliceContentGetter({
  filterKeys,
  sanitizeFilterValue,
  sliceTypes: ['retail_tray'],
});

const keyFilterValues = (obj, [key, values]) => ({
  ...obj,
  [key]: filterUnique([...(obj[key] || []), ...values]),
});

const filtersToKeyValuePairs = obj => Object
  .entries(obj)
  .filter(([key, value]) => apiFilterKeys.includes(key) && value)
  .map(([key, value]) => [
    key.replace('filter_', ''),
    stringToArray(value)
      .map(filter => sanitizeFilterValue(
        filter,
        key.replace('filter_', ''),
      )),
  ]);

const getRequestFilters = items => items
  .map(obj => filtersToKeyValuePairs(obj))
  .reduce((filters, values) => [...filters, ...values], [])
  .reduce(keyFilterValues, {});

const getMultipleRequestFilters = (items, pageSize) => items
  .map(obj => ({
    pageSize,
    filters: filtersToKeyValuePairs(obj)
      .reduce(keyFilterValues, {}),
  }));

const createSliceSettings = (filters, multipleRequests, { sliceId, type, pageSize }) => {
  const sliceSettings = {
    [sliceId]: {
      filters,
      pageSize: filters.skus ? filters.skus.length : pageSize,
    },
  };

  if (type === 'catalog_tray') {
    Object.assign(sliceSettings[sliceId], { multipleRequests });
  }
  return sliceSettings;
};

const getItemSetsFromList = sliceContent => {
  const { items = [] } = sliceContent;
  // eslint-disable-next-line
  const pageSize = sliceContent.product_count || 6;
  const multipleRequests = getMultipleRequestFilters(items, pageSize);
  const filters = getRequestFilters(items);

  if (filters && Object.keys(filters).length) {
    // TODO: I've kept this as per the current content block but renaming
    // `skus` to `sku` might make mapping a little cleaner here. Although
    // we do now accept a comma separated string
    // eslint-disable-next-line
    filters.skus = filters.sku;
    delete filters.sku;
    return createSliceSettings(filters, multipleRequests, { pageSize, ...sliceContent });
  }

  return undefined;
};

const getSliceType = sliceContent => {
  const defaultType = ['catalog_tray', 'retail_tray'].includes(sliceContent.type)
    ? 'Selected Products'
    : 'Filtered Products';
  return sliceContent.filtered_or_selected_products || defaultType;
};

const getItemSetsFromSponsoredProducts = sliceContent => ({
  [sliceContent.sliceId]: {
    // DEV NOTE: Flag we set to distinguish how sponsored products are fetched
    isSponsored: true,
    // DEV NOTE: Prismic slice fields
    segment: sliceContent.segment,
    brandPlacement: sliceContent.brand_placement,
  },
});

const getItemSetsFromSliceContent = sliceContent => {
  if (!SUPPORTED_SLICE_TYPES.includes(sliceContent.type)) {
    return undefined;
  }

  // DEV NOTE: Sponsored products where SKUs are populated by API
  if (sliceContent.type === SliceType.SPONSORED_BRAND_PRODUCTS) {
    return getItemSetsFromSponsoredProducts(sliceContent);
  }

  // DEV NOTE: Regular products setup where SKUs come from Prismic
  if (getSliceType(sliceContent) === 'Selected Products') {
    return getItemSetsFromList(sliceContent);
  }
  return {
    [sliceContent.sliceId]: {
      ...getItemSetsFromFilteredResultsSlice(sliceContent)[sliceContent.sliceId],
      pageSize: sliceContent.product_count || 6,
    },
  };
};

export default createFetchReducer({
  initialState,
  actionType: FETCH_PRODUCTS_TYPE,
  itemTypePlural: 'products',
  itemIdName: 'sku',
  getItemSetsFromSlices: createItemSetsFromSlicesGetter({
    getItemSetsFromSliceContent,
  }),
});
